"use client";

/**
 * Interactive demo page with stepper – shows booking flow, dashboard, calendar, and bookings table.
 */
import { useState } from "react";
import Link from "next/link";
import { BookingForm } from "../[businessSlug]/BookingForm";
import { formatBookingDateShort } from "@/lib/booking-display";

// Mock demo business data
const demoBusiness = {
  id: "demo-business-id",
  name: "Demo Hair Salon",
  businessType: "HAIR_SALON" as const,
  featureFlags: {
    requiresStaffSelection: true,
    allowsGroupBookings: false,
    requiresIntakeForm: false,
  },
  description: "Experience our booking system with this interactive demo. Select a service, choose a date and time, and see how easy it is for your customers to book appointments.",
  logoUrl: "https://images.unsplash.com/photo-1560066984-138dadb4c035?w=200&h=200&fit=crop&crop=center",
  bannerUrl: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=1200&h=400&fit=crop&crop=center",
  services: [
    { id: "demo-1", name: "Haircut", durationMinutes: 30, priceCents: 2500 },
    { id: "demo-2", name: "Haircut & Style", durationMinutes: 60, priceCents: 4500 },
    { id: "demo-3", name: "Color Treatment", durationMinutes: 120, priceCents: 8500 },
  ],
  availability: [
    { id: "demo-avail-1", dayOfWeek: 1, startTime: "09:00", endTime: "17:00", specificDate: null, isOff: false },
    { id: "demo-avail-2", dayOfWeek: 2, startTime: "09:00", endTime: "17:00", specificDate: null, isOff: false },
    { id: "demo-avail-3", dayOfWeek: 3, startTime: "09:00", endTime: "17:00", specificDate: null, isOff: false },
    { id: "demo-avail-4", dayOfWeek: 4, startTime: "09:00", endTime: "17:00", specificDate: null, isOff: false },
    { id: "demo-avail-5", dayOfWeek: 5, startTime: "09:00", endTime: "17:00", specificDate: null, isOff: false },
  ],
};

// Mock dashboard data
const mockDashboardData = {
  todayBookings: [
    { id: "demo-b1", startAt: new Date().toISOString().replace(/T.*/, "T10:00:00"), service: { name: "Haircut" }, customer: { name: "Sarah Johnson", email: "sarah@example.com" } },
    { id: "demo-b2", startAt: new Date().toISOString().replace(/T.*/, "T14:30:00"), service: { name: "Color Treatment" }, customer: { name: "Michael Chen", email: "michael@example.com" } },
    { id: "demo-b3", startAt: new Date().toISOString().replace(/T.*/, "T16:00:00"), service: { name: "Haircut & Style" }, customer: { name: "Emma Williams", email: "emma@example.com" } },
  ],
  upcomingBookings: [
    { id: "demo-b4", startAt: new Date(Date.now() + 86400000).toISOString().replace(/T.*/, "T09:00:00"), service: { name: "Haircut" }, customer: { name: "James Brown", email: "james@example.com" } },
    { id: "demo-b5", startAt: new Date(Date.now() + 86400000 * 2).toISOString().replace(/T.*/, "T11:00:00"), service: { name: "Haircut & Style" }, customer: { name: "Olivia Davis", email: "olivia@example.com" } },
    { id: "demo-b6", startAt: new Date(Date.now() + 86400000 * 3).toISOString().replace(/T.*/, "T15:00:00"), service: { name: "Color Treatment" }, customer: { name: "David Wilson", email: "david@example.com" } },
  ],
  totalBookings: 127,
  totalCustomers: 48,
  thisMonthBookings: 23,
  thisMonthRevenue: 57500, // £575.00
  popularServices: [
    { serviceId: "demo-1", serviceName: "Haircut", bookingCount: 12 },
    { serviceId: "demo-2", serviceName: "Haircut & Style", bookingCount: 8 },
    { serviceId: "demo-3", serviceName: "Color Treatment", bookingCount: 3 },
  ],
};

// Mock calendar events
const mockCalendarEvents = [
  { id: "demo-c1", title: "Haircut · Sarah Johnson", start: new Date().toISOString().replace(/T.*/, "T10:00:00"), end: new Date().toISOString().replace(/T.*/, "T10:30:00"), color: "#E65C5C" },
  { id: "demo-c2", title: "Color Treatment · Michael Chen", start: new Date().toISOString().replace(/T.*/, "T14:30:00"), end: new Date().toISOString().replace(/T.*/, "T16:30:00"), color: "#2563eb" },
  { id: "demo-c3", title: "Haircut & Style · Emma Williams", start: new Date().toISOString().replace(/T.*/, "T16:00:00"), end: new Date().toISOString().replace(/T.*/, "T17:00:00"), color: "#059669" },
  { id: "demo-c4", title: "Haircut · James Brown", start: new Date(Date.now() + 86400000).toISOString().replace(/T.*/, "T09:00:00"), end: new Date(Date.now() + 86400000).toISOString().replace(/T.*/, "T09:30:00"), color: "#E65C5C" },
  { id: "demo-c5", title: "Haircut & Style · Olivia Davis", start: new Date(Date.now() + 86400000 * 2).toISOString().replace(/T.*/, "T11:00:00"), end: new Date(Date.now() + 86400000 * 2).toISOString().replace(/T.*/, "T12:00:00"), color: "#059669" },
  { id: "demo-c6", title: "Color Treatment · David Wilson", start: new Date(Date.now() + 86400000 * 3).toISOString().replace(/T.*/, "T15:00:00"), end: new Date(Date.now() + 86400000 * 3).toISOString().replace(/T.*/, "T17:00:00"), color: "#2563eb" },
];

// Mock bookings table data
const mockBookingsTable = [
  { id: "demo-t1", startAt: new Date().toISOString().replace(/T.*/, "T10:00:00"), endAt: new Date().toISOString().replace(/T.*/, "T10:30:00"), service: { name: "Haircut" }, customer: { name: "Sarah Johnson", email: "sarah@example.com" }, status: "confirmed" },
  { id: "demo-t2", startAt: new Date().toISOString().replace(/T.*/, "T14:30:00"), endAt: new Date().toISOString().replace(/T.*/, "T16:30:00"), service: { name: "Color Treatment" }, customer: { name: "Michael Chen", email: "michael@example.com" }, status: "confirmed" },
  { id: "demo-t3", startAt: new Date().toISOString().replace(/T.*/, "T16:00:00"), endAt: new Date().toISOString().replace(/T.*/, "T17:00:00"), service: { name: "Haircut & Style" }, customer: { name: "Emma Williams", email: "emma@example.com" }, status: "pending" },
  { id: "demo-t4", startAt: new Date(Date.now() + 86400000).toISOString().replace(/T.*/, "T09:00:00"), endAt: new Date(Date.now() + 86400000).toISOString().replace(/T.*/, "T09:30:00"), service: { name: "Haircut" }, customer: { name: "James Brown", email: "james@example.com" }, status: "confirmed" },
  { id: "demo-t5", startAt: new Date(Date.now() + 86400000 * 2).toISOString().replace(/T.*/, "T11:00:00"), endAt: new Date(Date.now() + 86400000 * 2).toISOString().replace(/T.*/, "T12:00:00"), service: { name: "Haircut & Style" }, customer: { name: "Olivia Davis", email: "olivia@example.com" }, status: "confirmed" },
  { id: "demo-t6", startAt: new Date(Date.now() + 86400000 * 3).toISOString().replace(/T.*/, "T15:00:00"), endAt: new Date(Date.now() + 86400000 * 3).toISOString().replace(/T.*/, "T17:00:00"), service: { name: "Color Treatment" }, customer: { name: "David Wilson", email: "david@example.com" }, status: "pending" },
];

const stepLabels = {
  1: "Step 1: Try booking an appointment",
  2: "Step 2: See your dashboard overview",
  3: "Step 3: View bookings on the calendar",
  4: "Step 4: Manage bookings in a table",
};

export default function DemoPage() {
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      confirmed: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
      pending: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
      cancelled: "bg-destructive/10 text-destructive",
      completed: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
    };
    return statusColors[status] || "bg-muted text-muted-foreground";
  };

  return (
    <div className="min-h-screen bg-muted/20 flex flex-col">
      <div className="container max-w-6xl mx-auto px-4 py-8 flex-1 flex flex-col">
        {/* Header */}
        <div className="mb-6">
          <Link
            href="/"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            ← Return to home
          </Link>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs px-2 py-1 rounded bg-primary/10 text-primary font-medium">DEMO</span>
                <h1 className="text-2xl font-semibold">{demoBusiness.name}</h1>
              </div>
              <p className="text-muted-foreground text-sm">
                {stepLabels[step]}
              </p>
            </div>
            
            {/* Stepper */}
            <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
              {[1, 2, 3, 4].map((stepNum) => (
                <div key={stepNum} className="flex items-center gap-1 sm:gap-2">
                  <div className={`flex items-center gap-1 sm:gap-2 ${step === stepNum ? "text-primary" : "text-muted-foreground"}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step === stepNum ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                      {stepNum}
                    </div>
                    <span className="text-xs sm:text-sm font-medium hidden md:inline">
                      {stepNum === 1 ? "Booking" : stepNum === 2 ? "Dashboard" : stepNum === 3 ? "Calendar" : "Table"}
                    </span>
                  </div>
                  {stepNum < 4 && <div className="w-4 sm:w-8 h-0.5 bg-border" />}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex-1 flex flex-col min-h-[600px]">
          {/* Step 1: Booking Form */}
          {step === 1 && (
            <div className="space-y-6">
            <div className="rounded-xl border-2 border-dashed border-primary/30 bg-card/50 p-4">
              <p className="text-sm text-muted-foreground text-center">
                <strong>Demo mode:</strong> This is a demonstration. Bookings made here will not be processed.
              </p>
            </div>

            {/* Match actual booking page layout */}
            <div className="bg-muted/30 -mx-4 px-4 py-10 md:py-16 rounded-xl">
              <div className="flex flex-col md:grid md:grid-cols-2 md:gap-8 lg:gap-10 max-w-6xl mx-auto">
                {/* Left column: logo, title, description (card) */}
                <div className="order-1 md:order-1 rounded-xl border border-border bg-card shadow-sm overflow-hidden">
                  {demoBusiness.bannerUrl && (
                    <div className="relative w-full h-[calc(var(--spacing)*126)] sm:h-[calc(var(--spacing)*130)] overflow-hidden">
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img src={demoBusiness.bannerUrl} alt="" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-transparent" />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-start gap-4">
                      {demoBusiness.logoUrl && (
                        <div className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden border border-border bg-muted">
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img src={demoBusiness.logoUrl} alt="" className="w-full h-full object-cover" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h2 className="text-2xl sm:text-3xl font-bold text-foreground">{demoBusiness.name}</h2>
                        {demoBusiness.description && (
                          <p className="mt-2 text-sm sm:text-base text-muted-foreground leading-relaxed">
                            {demoBusiness.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right column: booking form */}
                <div className="order-2 md:order-2">
                  <BookingForm
                    businessId={demoBusiness.id}
                    businessName={demoBusiness.name}
                    businessType={demoBusiness.businessType}
                    featureFlags={demoBusiness.featureFlags}
                    services={demoBusiness.services}
                    availability={demoBusiness.availability}
                    isDemo={true}
                    onDemoSuccess={() => setStep(2)}
                  />
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Step 2: Dashboard Preview */}
          {step === 2 && (
            <div className="space-y-6">
            <div className="rounded-xl border-2 border-dashed border-primary/30 bg-card/50 p-4">
              <p className="text-sm text-muted-foreground text-center">
                <strong>Dashboard preview:</strong> This is how your bookings appear in your business dashboard.
              </p>
            </div>

            {/* Mock Dashboard */}
            <div className="space-y-8 bg-background rounded-xl border border-border p-6 shadow-lg">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">Dashboard</h1>
              </div>

              {/* Calendar + Stats */}
              <div className="grid gap-4 lg:grid-cols-3">
                <div className="lg:col-span-1">
                  <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
                    <h2 className="text-sm font-medium text-muted-foreground mb-3">Calendar</h2>
                    <div className="text-sm text-muted-foreground text-center py-8">
                      <svg className="w-16 h-16 mx-auto mb-2 text-muted-foreground/40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p>Calendar view</p>
                    </div>
                  </div>
                </div>
                <div className="lg:col-span-2 grid gap-4 md:grid-cols-2">
                  <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
                    <h2 className="text-sm font-medium text-muted-foreground">Today&apos;s Bookings</h2>
                    <p className="text-2xl font-semibold mt-1 text-foreground">{mockDashboardData.todayBookings.length}</p>
                    <ul className="mt-2 space-y-1 text-sm">
                      {mockDashboardData.todayBookings.map((b) => (
                        <li key={b.id} className="text-muted-foreground">
                          {new Date(b.startAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}{" "}
                          {b.service.name}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
                    <h2 className="text-sm font-medium text-muted-foreground">Total Bookings</h2>
                    <p className="text-2xl font-semibold text-foreground">{mockDashboardData.totalBookings}</p>
                  </div>

                  <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
                    <h2 className="text-sm font-medium text-muted-foreground">This Month Revenue</h2>
                    <p className="text-2xl font-semibold text-foreground">£{(mockDashboardData.thisMonthRevenue / 100).toFixed(2)}</p>
                    <p className="text-xs mt-1 text-green-600">↑ 15.2% vs last month</p>
                  </div>

                  <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
                    <h2 className="text-sm font-medium text-muted-foreground">Total Customers</h2>
                    <p className="text-2xl font-semibold text-foreground">{mockDashboardData.totalCustomers}</p>
                  </div>
                </div>
              </div>

              {/* Monthly Stats */}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
                  <h2 className="text-sm font-medium text-muted-foreground mb-2">This Month</h2>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Bookings:</span>
                      <span className="font-semibold">{mockDashboardData.thisMonthBookings}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Revenue:</span>
                      <span className="font-semibold">£{(mockDashboardData.thisMonthRevenue / 100).toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
                  <h2 className="text-sm font-medium text-muted-foreground mb-2">Popular Services (This Month)</h2>
                  <ul className="space-y-2">
                    {mockDashboardData.popularServices.map((s) => (
                      <li key={s.serviceId} className="flex justify-between text-sm">
                        <span>{s.serviceName}</span>
                        <span className="font-medium">{s.bookingCount} booking{s.bookingCount !== 1 ? "s" : ""}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Upcoming Bookings */}
              <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
                <h2 className="text-sm font-medium text-muted-foreground mb-2">Upcoming (Next 7 Days)</h2>
                <ul className="space-y-2 text-sm">
                  {mockDashboardData.upcomingBookings.map((b) => {
                    const { dateLine, timeLine } = formatBookingDateShort(b.startAt, b.startAt);
                    return (
                      <li key={b.id} className="flex items-center justify-between">
                        <div>
                          <span className="font-medium">{dateLine}</span>
                          {timeLine && <> {" "}{timeLine}</>}
                          {" · "}
                          {b.service.name} · {b.customer.name ?? b.customer.email}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
          )}

          {/* Step 3: Calendar Preview */}
          {step === 3 && (
            <div className="space-y-6">
            <div className="rounded-xl border-2 border-dashed border-primary/30 bg-card/50 p-4">
              <p className="text-sm text-muted-foreground text-center">
                <strong>Calendar view:</strong> Visualize all your bookings on a calendar with month, week, and day views.
              </p>
            </div>

            {/* Mock Calendar */}
            <div className="bg-background rounded-xl border border-border p-6 shadow-lg">
              <div className="mb-4 flex items-center justify-between">
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">Calendar</h1>
                <div className="flex gap-2">
                  <button className="px-3 py-1.5 text-xs sm:text-sm rounded bg-primary text-primary-foreground font-medium">Month</button>
                  <button className="px-3 py-1.5 text-xs sm:text-sm rounded text-muted-foreground hover:bg-muted font-medium">Week</button>
                  <button className="px-3 py-1.5 text-xs sm:text-sm rounded text-muted-foreground hover:bg-muted font-medium">Day</button>
                </div>
              </div>

              {/* Calendar Grid Mockup */}
              <div className="border border-border rounded-lg overflow-hidden">
                {/* Calendar Header */}
                <div className="bg-muted/50 border-b border-border px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <button className="p-1 hover:bg-muted rounded">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <span className="text-sm font-semibold text-foreground">February 2026</span>
                    <button className="p-1 hover:bg-muted rounded">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Calendar Days */}
                <div className="p-4">
                  {/* Day headers */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                      <div key={day} className="text-xs font-medium text-muted-foreground text-center py-2">
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Calendar days with events */}
                  <div className="grid grid-cols-7 gap-1">
                    {/* Empty days */}
                    {[1, 2, 3].map((i) => (
                      <div key={`empty-${i}`} className="aspect-square rounded border border-transparent" />
                    ))}

                    {/* Days with bookings */}
                    {[
                      { day: 4, events: [] },
                      { day: 5, events: [] },
                      { day: 6, events: [{ title: "Haircut · Sarah", time: "10:00", color: "#E65C5C" }] },
                      { day: 7, events: [] },
                      { day: 8, events: [{ title: "Color · Michael", time: "14:30", color: "#2563eb" }] },
                      { day: 9, events: [] },
                      { day: 10, events: [{ title: "Haircut · Sarah", time: "10:00", color: "#E65C5C" }, { title: "Style · Emma", time: "16:00", color: "#059669" }] },
                    ].map(({ day, events }) => (
                      <div
                        key={day}
                        className={`aspect-square rounded border ${day === 6 ? "border-primary bg-primary/5" : "border-border"} p-1 flex flex-col gap-0.5`}
                      >
                        <span className={`text-xs ${day === 6 ? "font-semibold text-primary" : "text-muted-foreground"}`}>
                          {day}
                        </span>
                        {events.map((event, idx) => (
                          <div
                            key={idx}
                            className="text-white text-[10px] px-1 py-0.5 rounded truncate"
                            style={{ backgroundColor: event.color }}
                            title={`${event.title} - ${event.time}`}
                          >
                            {event.time} {event.title.split(" · ")[0]}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Step 4: Bookings Table */}
          {step === 4 && (
            <div className="space-y-6">
            <div className="rounded-xl border-2 border-dashed border-primary/30 bg-card/50 p-4">
              <p className="text-sm text-muted-foreground text-center">
                <strong>Bookings table:</strong> Manage all your bookings in a detailed table view with filtering and actions.
              </p>
            </div>

            {/* Mock Bookings Table */}
            <div className="bg-background rounded-xl border border-border shadow-lg overflow-hidden">
              <div className="p-6 border-b border-border flex items-center justify-between">
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">Bookings</h1>
                <button className="inline-flex items-center justify-center rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm hover:opacity-95 transition-opacity">
                  Create Booking
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      <th className="p-3 text-left font-medium">Date & Time</th>
                      <th className="p-3 text-left font-medium">Service</th>
                      <th className="p-3 text-left font-medium">Customer</th>
                      <th className="p-3 text-left font-medium">Status</th>
                      <th className="p-3 text-left font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mockBookingsTable.map((b) => {
                      const { dateLine, timeLine } = formatBookingDateShort(b.startAt, b.endAt);
                      return (
                        <tr key={b.id} className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors">
                          <td className="p-3">
                            <div className="font-medium">{dateLine}</div>
                            {timeLine && (
                              <div className="text-xs text-muted-foreground">{timeLine}</div>
                            )}
                          </td>
                          <td className="p-3">{b.service.name}</td>
                          <td className="p-3">
                            <div>{b.customer.name || b.customer.email}</div>
                            {b.customer.name && (
                              <div className="text-xs text-muted-foreground">{b.customer.email}</div>
                            )}
                          </td>
                          <td className="p-3">
                            <span className={`text-xs px-2 py-1 rounded capitalize ${getStatusBadge(b.status)}`}>
                              {b.status}
                            </span>
                          </td>
                          <td className="p-3">
                            <div className="flex flex-wrap gap-3">
                              <button className="text-sm text-primary hover:underline">
                                View
                              </button>
                              {b.status === "pending" && (
                                <button className="text-sm text-green-600 hover:underline">
                                  Approve
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          )}
        </div>

        {/* Navigation - Fixed position at bottom */}
        <div className="flex items-center justify-between pt-6 mt-auto border-t border-border">
          <button
            onClick={() => setStep(Math.max(1, step - 1) as 1 | 2 | 3 | 4)}
            disabled={step === 1}
            className={`inline-flex items-center gap-2 rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium transition-colors ${
              step === 1
                ? "text-muted-foreground opacity-50 cursor-not-allowed"
                : "hover:bg-muted"
            }`}
          >
            ← Previous
          </button>
          {step < 4 ? (
            <button
              onClick={() => setStep(Math.min(4, step + 1) as 1 | 2 | 3 | 4)}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-2 text-sm font-semibold text-primary-foreground shadow-sm hover:opacity-95 transition-opacity"
            >
              Next →
            </button>
          ) : (
            <Link
              href="/auth/signup"
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-2 text-sm font-semibold text-primary-foreground shadow-sm hover:opacity-95 transition-opacity"
            >
              Get started →
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
